<template>
  <div>
    <!-- Skeleton loader -->
    <b-card no-body class="student-card mb-2" v-if="detailLoading">
      <b-card-body
        class="p-4 d-flex align-items-center justify-content-center"
        style="min-height: 400px"
      >
        <div class="text-center w-100">
          <div
            class="skeleton-loader mx-auto mb-2"
            style="width: 140px; height: 140px; border-radius: 50%"
          ></div>
          <div
            class="skeleton-loader mx-auto mb-1"
            style="width: 200px; height: 30px"
          ></div>
          <div
            class="skeleton-loader mx-auto mb-1"
            style="width: 150px; height: 20px"
          ></div>
        </div>
      </b-card-body>
    </b-card>

    <b-card no-body class="student-card mb-2" v-else-if="detail">
      <div class="gradient-top-line"></div>
      <b-card-body class="p-0">
        <b-row class="m-0">
          <!-- Left Profile Sidebar -->
          <b-col
            cols="12"
            lg="4"
            xl="3"
            class="profile-sidebar border-right p-0 text-center bg-white position-relative"
          >
            <div class="profile-banner text-left px-2 pt-2">
              <b-button
                variant="light"
                size="sm"
                @click="goBack"
                class="back-btn shadow-sm font-weight-bold text-primary"
                style="position: relative; z-index: 10"
              >
                <feather-icon icon="ArrowLeftIcon" class="mr-50" />
                {{ $t("Back to Grid") }}
              </b-button>
            </div>

            <div class="avatar-wrapper mx-auto mb-2" style="margin-top: -60px">
              <b-avatar
                :src="getPicturePreview(student.picture)"
                size="130"
                variant="light-primary"
                class="shadow-lg profile-avatar"
                referrerpolicy="no-referrer"
              />
              <div
                class="status-indicator"
                :class="statusIndicatorClass"
                v-b-tooltip.hover
                :title="statusIndicatorTitle"
              ></div>
            </div>

            <div class="px-3 pb-3">
              <p
                v-if="federalRegLabel"
                class="mb-1 font-weight-bolder text-primary small text-uppercase"
                style="letter-spacing: 0.06em"
              >
                {{ federalRegLabel }}
              </p>
              <h3 class="font-weight-bolder mb-0 text-primary-dark name-title">
                {{ student.name || "N/A" }}
              </h3>
              <p class="text-muted designation-text mb-1">
                {{ $t("F/N") }}: {{ student.father_name || "—" }}
              </p>
              <div v-if="classLabel" class="mb-2">
                <b-badge
                  variant="light-info"
                  class="text-uppercase font-weight-bolder px-1"
                >
                  {{ classLabel }}
                </b-badge>
              </div>

              <div class="d-flex flex-wrap justify-content-center gap-2 mb-2">
                <b-badge
                  :variant="
                    scholarshipVariant(studentDetails.scholarshipStatus)
                  "
                  class="text-uppercase py-1 px-2"
                >
                  {{
                    studentDetails.scholarshipStatus || $t("Scholarship N/A")
                  }}
                </b-badge>
                <b-badge
                  :variant="statusVariant(studentDetails.studentStatus)"
                  class="text-uppercase py-1 px-2"
                >
                  {{ studentDetails.studentStatus || $t("Status N/A") }}
                </b-badge>
              </div>

              <div class="d-flex flex-column gap-2 mt-2 p-2">
                <div class="info-pill text-left" v-if="student.contact_student">
                  <feather-icon
                    icon="SmartphoneIcon"
                    size="16"
                    class="mr-50 text-primary"
                  />
                  <span>{{ student.contact_student }}</span>
                </div>
                <div class="info-pill text-left" v-if="student.contact_father">
                  <feather-icon
                    icon="MessageCircleIcon"
                    size="16"
                    class="mr-50 text-primary"
                  />
                  <span>{{ student.contact_father }}</span>
                </div>
                <div class="info-pill text-left" v-if="campusLabel">
                  <feather-icon
                    icon="HomeIcon"
                    size="16"
                    class="mr-50 text-primary"
                  />
                  <span>{{ campusLabel }}</span>
                </div>
                <div
                  class="info-pill text-left"
                  v-if="studentDetails.branchCode"
                >
                  <feather-icon
                    icon="HashIcon"
                    size="16"
                    class="mr-50 text-primary"
                  />
                  <span>{{ studentDetails.branchCode }}</span>
                </div>
                <div class="info-pill text-left" v-if="studentDetails.school">
                  <feather-icon
                    icon="MapPinIcon"
                    size="16"
                    class="mr-50 text-primary"
                  />
                  <span>{{ studentDetails.school }}</span>
                </div>
                <div class="info-pill text-left" v-if="docSummary">
                  <feather-icon
                    icon="FolderIcon"
                    size="16"
                    class="mr-50 text-primary"
                  />
                  <span>{{ docSummary }}</span>
                </div>
              </div>
            </div>
          </b-col>

          <!-- Right Content -->
          <b-col cols="12" lg="8" xl="9" class="p-3 student-details-tabs-col">
            <div class="student-details-tabs-shell">
              <b-tabs
                v-model="activeDetailTab"
                nav-class="custom-nav-tabs"
                content-class="student-details-tab-panes"
                active-nav-item-class="custom-nav-active"
                no-fade
              >
                <!-- Info Tab -->
                <b-tab :title="$t('Info')">
                  <div class="tab-content-wrapper p-2 animation-fade">
                    <h5 class="section-title">
                      <feather-icon icon="UserIcon" class="mr-50" />
                      {{ $t("Basic Information") }}
                    </h5>
                    <b-row class="mb-3 content-grid">
                      <b-col
                        v-for="field in basicFields"
                        :key="field.key"
                        sm="6"
                        lg="4"
                        class="mb-2"
                      >
                        <label class="info-label">{{ field.label }}</label>
                        <div v-if="field.badge">
                          <b-badge
                            :variant="field.badgeVariant"
                            class="text-uppercase py-1 px-2 mt-50"
                          >
                            {{ field.value }}
                          </b-badge>
                        </div>
                        <div v-else class="info-value">{{ field.value }}</div>
                      </b-col>
                    </b-row>

                    <h5 class="section-title mt-2">
                      <feather-icon icon="MapPinIcon" class="mr-50" />
                      {{ $t("Location & Branch") }}
                    </h5>
                    <b-row class="mb-3 content-grid">
                      <b-col
                        v-for="field in locationFields"
                        :key="field.key"
                        sm="6"
                        lg="4"
                        class="mb-2"
                      >
                        <label class="info-label">{{ field.label }}</label>
                        <div class="info-value">{{ field.value }}</div>
                      </b-col>
                    </b-row>

                    <h5 class="section-title mt-2">
                      <feather-icon icon="BookIcon" class="mr-50" />
                      {{ $t("Academic Information") }}
                    </h5>
                    <b-row class="mb-3 content-grid">
                      <b-col
                        v-for="field in academicFields"
                        :key="field.key"
                        sm="6"
                        lg="4"
                        class="mb-2"
                      >
                        <label class="info-label">{{ field.label }}</label>
                        <div class="info-value">{{ field.value }}</div>
                      </b-col>
                    </b-row>

                    <h5 class="section-title mt-2">
                      <feather-icon icon="AwardIcon" class="mr-50" />
                      {{ $t("Scholarship & Status") }}
                    </h5>
                    <b-row class="mb-3 content-grid">
                      <b-col
                        v-for="field in statusFields"
                        :key="field.key"
                        sm="6"
                        lg="4"
                        class="mb-2"
                      >
                        <label class="info-label">{{ field.label }}</label>
                        <div v-if="field.badge">
                          <b-badge
                            v-if="field.isDate"
                            :variant="field.badgeVariant"
                            class="text-uppercase py-1 px-2 mt-50"
                          >
                            {{
                              new Date(field.value).toLocaleDateString(
                                "en-UK",
                                {
                                  year: "numeric",
                                  day: "numeric",
                                  month: "short",
                                }
                              )
                            }}
                          </b-badge>
                          <b-badge
                            v-else
                            :variant="field.badgeVariant"
                            class="text-uppercase py-1 px-2 mt-50"
                          >
                            {{ field.value }}
                          </b-badge>
                        </div>
                        <div v-else class="info-value">{{ field.value }}</div>
                      </b-col>
                    </b-row>

                    <div v-if="commentText">
                      <h5 class="section-title mt-2">
                        <feather-icon icon="MessageSquareIcon" class="mr-50" />
                        {{ $t("Comment") }}
                      </h5>
                      <b-row class="content-grid">
                        <b-col cols="12" class="mb-2">
                          <div class="info-value">{{ commentText }}</div>
                        </b-col>
                      </b-row>
                    </div>
                  </div>
                </b-tab>

                <!-- Documents Tab -->
                <b-tab :title="$t('Documents')">
                  <div
                    class="tab-content-wrapper scrollable-tab-content p-2 animation-fade"
                  >
                    <div v-if="docsLoading" class="text-center py-5">
                      <b-spinner variant="primary" />
                      <p class="text-muted mt-1 mb-0">
                        {{ $t("Loading documents…") }}
                      </p>
                    </div>

                    <template v-else>
                      <div
                        class="docs-header d-flex flex-wrap justify-content-between align-items-center mb-2"
                      >
                        <div>
                          <h5 class="section-title mb-50 border-0 pb-0">
                            <feather-icon icon="FolderIcon" class="mr-50" />
                            {{ $t("Student Documents") }}
                          </h5>
                          <p class="text-muted mb-0 docs-subtitle">
                            {{ $t("View and open uploaded files") }}
                          </p>
                        </div>
                        <div
                          class="docs-stats d-flex flex-wrap"
                          v-if="typeWiseDocuments.length"
                        >
                          <span class="docs-stat-pill docs-stat-pill--total">
                            <feather-icon icon="LayersIcon" size="14" />
                            {{ typeWiseDocuments.length }} {{ $t("Types") }}
                          </span>
                          <span
                            v-if="docStats.notUploaded"
                            class="docs-stat-pill docs-stat-pill--missing"
                          >
                            <feather-icon icon="UploadCloudIcon" size="14" />
                            {{ docStats.notUploaded }} {{ $t("Not Uploaded") }}
                          </span>
                          <span
                            v-if="docStats.approved"
                            class="docs-stat-pill docs-stat-pill--approved"
                          >
                            <feather-icon icon="CheckCircleIcon" size="14" />
                            {{ docStats.approved }} {{ $t("Approved") }}
                          </span>
                          <span
                            v-if="docStats.pending"
                            class="docs-stat-pill docs-stat-pill--pending"
                          >
                            <feather-icon icon="ClockIcon" size="14" />
                            {{ docStats.pending }} {{ $t("Pending") }}
                          </span>
                          <span
                            v-if="docStats.rejected"
                            class="docs-stat-pill docs-stat-pill--rejected"
                          >
                            <feather-icon icon="XCircleIcon" size="14" />
                            {{ docStats.rejected }} {{ $t("Rejected") }}
                          </span>
                        </div>
                      </div>

                      <div
                        v-if="typeWiseDocuments.length === 0"
                        class="text-center p-5 empty-state-box"
                      >
                        <div class="empty-docs-icon-wrap mx-auto mb-2">
                          <feather-icon
                            icon="FolderIcon"
                            size="36"
                            class="text-primary"
                          />
                        </div>
                        <h4 class="empty-state-title mb-50">
                          {{ $t("No Document Types") }}
                        </h4>
                        <p class="empty-state-message text-muted mb-0">
                          {{
                            $t(
                              "No document types are configured for this student."
                            )
                          }}
                        </p>
                      </div>

                      <div v-else class="docs-type-grid">
                        <div
                          v-for="(doc, i) in typeWiseDocuments"
                          :key="'doc-' + (doc.id || doc.docTypeID || i)"
                          class="docs-type-card"
                          :class="{
                            'docs-type-card--uploaded': !!doc.url,
                            'docs-type-card--missing': !doc.url,
                          }"
                        >
                          <div
                            class="docs-type-card__preview"
                            :class="{
                              'docs-type-card__preview--clickable': !!doc.url,
                            }"
                            @click="doc.url && openFile(doc.url)"
                          >
                            <img
                              v-if="doc.url && isImage(doc.url)"
                              :src="getDocPreviewUrl(doc.url)"
                              class="docs-type-card__img"
                              referrerpolicy="no-referrer"
                              alt="preview"
                              @error="onDocPreviewError($event, doc.url)"
                            />
                            <div
                              v-else-if="doc.url"
                              class="docs-type-card__file-icon"
                            >
                              <feather-icon
                                icon="FileTextIcon"
                                size="36"
                                class="text-primary"
                              />
                            </div>
                            <div v-else class="docs-type-card__empty">
                              <feather-icon
                                icon="UploadCloudIcon"
                                size="32"
                                class="text-muted"
                              />
                            </div>

                            <div v-if="doc.url" class="docs-type-card__overlay">
                              <feather-icon
                                icon="ExternalLinkIcon"
                                size="16"
                                class="text-white"
                              />
                            </div>
                          </div>

                          <div class="docs-type-card__body">
                            <div
                              class="docs-type-card__title"
                              v-b-tooltip.hover.top
                              :title="docTitle(doc)"
                            >
                              {{ docTitle(doc) }}
                            </div>
                            <b-badge
                              :variant="docStatusVariant(doc)"
                              class="docs-status-badge text-uppercase"
                            >
                              {{ docStatusLabel(doc) }}
                            </b-badge>
                            <b-button
                              v-if="doc.url"
                              size="sm"
                              variant="outline-primary"
                              class="docs-open-btn mt-50"
                              block
                              @click="openFile(doc.url)"
                            >
                              <feather-icon
                                icon="EyeIcon"
                                size="14"
                                class="mr-25"
                              />
                              {{ $t("View") }}
                            </b-button>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </b-tab>

                <!-- Zakat Tab -->
                <b-tab :title="$t('Zakat')">
                  <div
                    class="tab-content-wrapper scrollable-tab-content p-2 animation-fade zakat-panel"
                    :class="{ 'zakat-panel--rtl': zakatLang === 'ur' }"
                  >
                    <div
                      class="zakat-header d-flex flex-wrap justify-content-between align-items-start mb-2"
                    >
                      <div class="zakat-header__text">
                        <h5
                          class="section-title mb-50 border-0 pb-0 zakat-header__title"
                        >
                          <feather-icon
                            icon="HeartIcon"
                            size="14"
                            class="zakat-header__icon"
                          />
                          {{ zakatContent.title }}
                        </h5>
                        <p class="text-muted mb-0 zakat-subtitle">
                          {{ zakatContent.subtitle }}
                        </p>
                      </div>
                      <div class="zakat-lang-toggle zakat-header__toggle">
                        <button
                          type="button"
                          class="zakat-lang-btn"
                          :class="{
                            'zakat-lang-btn--active': zakatLang === 'en',
                          }"
                          @click="zakatLang = 'en'"
                        >
                          EN
                        </button>
                        <button
                          type="button"
                          class="zakat-lang-btn"
                          :class="{
                            'zakat-lang-btn--active': zakatLang === 'ur',
                          }"
                          @click="zakatLang = 'ur'"
                        >
                          اردو
                        </button>
                      </div>
                    </div>

                    <div class="zakat-status-card mb-2">
                      <div class="zakat-status-card__item">
                        <span class="zakat-status-card__label">
                          {{ $t("Eligibility") }}
                        </span>
                        <b-badge
                          :variant="zakatStatusVariant"
                          class="text-uppercase py-1 px-2"
                        >
                          {{ zakatStatusLabel }}
                        </b-badge>
                      </div>
                    </div>

                    <div class="zakat-form-card">
                      <div class="zakat-info-box">
                        {{ zakatContent.infoBox }}
                      </div>

                      <ul class="zakat-criteria-list">
                        <li
                          v-for="(item, index) in zakatContent.bullets"
                          :key="'zakat-bullet-' + index"
                          class="zakat-criteria-item"
                        >
                          <span class="zakat-criteria-item__main">
                            {{ item.text }}
                          </span>
                          <ul
                            v-if="item.subItems && item.subItems.length"
                            class="zakat-sub-list"
                          >
                            <li
                              v-for="(subItem, subIndex) in item.subItems"
                              :key="'zakat-sub-' + index + '-' + subIndex"
                            >
                              {{ subItem }}
                            </li>
                          </ul>
                        </li>
                      </ul>

                      <div
                        class="zakat-response-display"
                        :class="{
                          'zakat-response-display--yes':
                            normalizedZakatEligible.toLowerCase() === 'yes',
                          'zakat-response-display--no':
                            normalizedZakatEligible.toLowerCase() === 'no',
                          'zakat-response-display--na': !hasZakatResponse,
                        }"
                      >
                        {{ zakatSelectedOptionText }}
                      </div>
                    </div>
                  </div>
                </b-tab>

                <!-- Request Tab -->
                <b-tab :title="$t('Request')">
                  <div class="tab-content-wrapper p-2 animation-fade">
                    <div
                      v-if="studentLeftRequests.length === 0"
                      class="text-center p-5 empty-state-box"
                    >
                      <div class="empty-docs-icon-wrap mx-auto mb-2">
                        <feather-icon
                          icon="InboxIcon"
                          size="36"
                          class="text-primary"
                        />
                      </div>
                      <h4 class="empty-state-title mb-50">
                        {{ $t("No Requests") }}
                      </h4>
                      <p class="empty-state-message text-muted mb-0">
                        {{
                          $t(
                            "No leaving or transfer requests found for this student."
                          )
                        }}
                      </p>
                    </div>

                    <template v-else>
                      <h5 class="section-title mb-2">
                        <feather-icon icon="FileTextIcon" class="mr-50" />
                        {{ $t("Student Requests") }}
                      </h5>
                      <div
                        v-for="(req, index) in studentLeftRequests"
                        :key="req.id || index"
                        class="request-block"
                        :class="{ 'request-block--spaced': index > 0 }"
                      >
                        <p
                          v-if="studentLeftRequests.length > 1"
                          class="request-block__heading mb-1"
                        >
                          {{ $t("Request") }} {{ index + 1 }}
                        </p>
                        <RequestDetails :reqItem="req" />
                      </div>
                    </template>
                  </div>
                </b-tab>

                <!-- utp -->
                <b-tab v-if="hasUtpDetails" :title="$t('UTP')">
                  <div class="tab-content-wrapper p-2 animation-fade">
                    <h5 class="section-title">
                      <feather-icon icon="AwardIcon" class="mr-50" />
                      {{ $t("UTP Details") }}
                    </h5>
                    <b-row class="mb-3 content-grid">
                      <b-col
                        v-for="field in utpFields"
                        :key="field.key"
                        sm="6"
                        lg="4"
                        class="mb-2"
                      >
                        <label class="info-label">{{ field.label }}</label>
                        <div v-if="field.badge">
                          <b-badge
                            :variant="field.badgeVariant"
                            class="text-uppercase py-1 px-2 mt-50"
                          >
                            {{ field.value }}
                          </b-badge>
                        </div>
                        <div v-else class="info-value">{{ field.value }}</div>
                      </b-col>
                    </b-row>
                  </div>
                </b-tab>

                <!-- attendance -->
                <b-tab :title="$t('Attendance')">
                  <div class="tab-content-wrapper p-2 animation-fade">
                    <div v-if="attLoading" class="text-center py-5">
                      <b-spinner variant="primary" />
                      <p class="text-muted mt-1 mb-0">
                        {{ $t("Loading attendance") }}
                      </p>
                    </div>

                    <div
                      v-else-if="attRecords.length === 0"
                      class="text-center p-5 empty-state-box"
                    >
                      <div class="empty-docs-icon-wrap mx-auto mb-2">
                        <feather-icon
                          icon="InboxIcon"
                          size="36"
                          class="text-primary"
                        />
                      </div>
                      <h4 class="empty-state-title mb-50">
                        {{ $t("No Attendance Record") }}
                      </h4>
                      <p class="empty-state-message text-muted mb-0">
                        {{ $t("No attendance record found for this student.") }}
                      </p>
                    </div>

                    <template v-else>
                      <h5 class="section-title mb-2">
                        <feather-icon icon="CalendarIcon" class="mr-50" />
                        {{ $t("Attendance") }}
                      </h5>
                      <div
                        class="attendance-container"
                      >
                        <div
                          class="d-none"
                        ></div>

                        <!-- Attendance Summary Card -->
                        <div
                          v-if="attSummary"
                          class="attendance-summary-card mb-2 mb-md-3"
                        >
                          <div class="summary-bg-shape summary-bg-shape--1"></div>
                          <div class="summary-bg-shape summary-bg-shape--2"></div>

                          <div class="summary-card-inner">
                            <div
                              class="d-flex justify-content-between align-items-center mb-1 flex-wrap"
                            >
                              <div class="d-flex align-items-center">
                                <div class="summary-icon-badge mr-75">
                                  <feather-icon icon="TrendingUpIcon" size="22" />
                                </div>
                                <div>
                                  <h5 class="text-white mb-25 font-weight-bolder">
                                    {{ $t("Attendance Summary") }}
                                  </h5>
                                  <span class="summary-subtitle">
                                    {{ $t("Cumulative attendance record across all months") }}
                                  </span>
                                </div>
                              </div>
                              <div class="summary-rate-badge mt-50 mt-sm-0">
                                <span class="summary-rate-label">{{ $t("Avg Attendance") }}</span>
                                <span class="summary-rate-val">{{ formatAttendancePercentage(attSummary.avgPerc) }}%</span>
                              </div>
                            </div>

                            <b-row class="summary-stats-row mt-1">
                              <b-col cols="6" md="3" class="mb-1 mb-md-0">
                                <div class="summary-metric-pill">
                                  <div class="summary-metric-icon summary-metric-icon--rate">
                                    <feather-icon icon="PieChartIcon" size="18" />
                                  </div>
                                  <div>
                                    <div class="summary-metric-value">
                                      {{ formatAttendancePercentage(attSummary.avgPerc) }}%
                                    </div>
                                    <div class="summary-metric-label">{{ $t("Average Rate") }}</div>
                                  </div>
                                </div>
                              </b-col>
                              <b-col cols="6" md="3" class="mb-1 mb-md-0">
                                <div class="summary-metric-pill">
                                  <div class="summary-metric-icon summary-metric-icon--present">
                                    <feather-icon icon="CheckCircleIcon" size="18" />
                                  </div>
                                  <div>
                                    <div class="summary-metric-value">
                                      {{ attSummary.presentTotal != null ? attSummary.presentTotal : 0 }}
                                    </div>
                                    <div class="summary-metric-label">{{ $t("Total Present") }}</div>
                                  </div>
                                </div>
                              </b-col>
                              <b-col cols="6" md="3" class="mb-1 mb-md-0">
                                <div class="summary-metric-pill">
                                  <div class="summary-metric-icon summary-metric-icon--absent">
                                    <feather-icon icon="XCircleIcon" size="18" />
                                  </div>
                                  <div>
                                    <div class="summary-metric-value">
                                      {{ attSummary.absentTotal != null ? attSummary.absentTotal : 0 }}
                                    </div>
                                    <div class="summary-metric-label">{{ $t("Total Absent") }}</div>
                                  </div>
                                </div>
                              </b-col>
                              <b-col cols="6" md="3" class="mb-1 mb-md-0">
                                <div class="summary-metric-pill">
                                  <div class="summary-metric-icon summary-metric-icon--days">
                                    <feather-icon icon="CalendarIcon" size="18" />
                                  </div>
                                  <div>
                                    <div class="summary-metric-value">
                                      {{ attSummary.daysTotal != null ? attSummary.daysTotal : 0 }}
                                    </div>
                                    <div class="summary-metric-label">{{ $t("Total Days") }}</div>
                                  </div>
                                </div>
                              </b-col>
                            </b-row>
                          </div>
                        </div>

                        <div
                          class="d-flex justify-content-between align-items-center mb-2 flex-wrap"
                        >
                          <div class="d-flex align-items-center">
                            <span class="attendance-section-accent mr-50"></span>
                            <div>
                              <h5 class="mb-0 font-weight-bolder text-primary-dark">
                                {{ $t("Monthly Breakdown") }}
                              </h5>
                              <small class="text-muted">
                                {{ $t("Track attendance month by month") }}
                              </small>
                            </div>
                          </div>
                          <b-badge
                            variant="light-primary"
                            pill
                            class="px-1 py-50 font-weight-bold"
                          >
                            {{ attRecords.length }}
                            {{
                              attRecords.length === 1
                                ? $t("Month")
                                : $t("Months")
                            }}
                          </b-badge>
                        </div>

                        <b-row>
                          <b-col
                            v-for="(att, index) in attRecords"
                            :key="'att-col-' + (att.id || index)"
                            cols="12"
                            md="6"
                            class="mb-2"
                          >
                            <div
                              class="attendance-card"
                              :class="
                                'attendance-card--' +
                                getAttendanceColorClass(att.percentage)
                              "
                            >
                              <div
                                class="d-flex align-items-center justify-content-between"
                              >
                                <div class="d-flex align-items-center">
                                  <div
                                    class="attendance-icon-wrap"
                                    :class="
                                      'attendance-icon-wrap--' +
                                      getAttendanceColorClass(att.percentage)
                                    "
                                  >
                                    <feather-icon icon="CalendarIcon" size="22" />
                                  </div>
                                  <div class="ml-1">
                                    <h6 class="mb-25 font-weight-bolder text-dark">
                                      {{ getMonthName(att.month) }} {{ att.year }}
                                    </h6>
                                    <span class="text-muted font-small-3">
                                      {{ att.totalDays }} {{ $t("working days") }}
                                    </span>
                                  </div>
                                </div>

                                <div class="attendance-circle-wrap">
                                  <svg
                                    viewBox="0 0 36 36"
                                    class="attendance-circular-chart"
                                  >
                                    <path
                                      class="circle-bg"
                                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <path
                                      class="circle"
                                      :class="
                                        'circle--' +
                                        getAttendanceColorClass(att.percentage)
                                      "
                                      :stroke-dasharray="`${Math.min(
                                        Math.max(att.percentage || 0, 0),
                                        100
                                      )}, 100`"
                                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                  </svg>
                                  <span
                                    class="attendance-circle-percentage"
                                    :class="
                                      'text--' +
                                      getAttendanceColorClass(att.percentage)
                                    "
                                  >
                                    {{ formatAttendancePercentage(att.percentage) }}%
                                  </span>
                                </div>
                              </div>

                              <div class="attendance-stats-box">
                                <div class="attendance-stat-col text-center">
                                  <div
                                    class="attendance-stat-value text-success font-weight-bolder"
                                  >
                                    {{
                                      att.presentDays != null
                                        ? att.presentDays
                                        : 0
                                    }}
                                  </div>
                                  <div class="attendance-stat-label">
                                    {{ $t("Present") }}
                                  </div>
                                </div>
                                <div class="attendance-stat-divider"></div>
                                <div class="attendance-stat-col text-center">
                                  <div
                                    class="attendance-stat-value text-danger font-weight-bolder"
                                  >
                                    {{
                                      att.absentDays != null
                                        ? att.absentDays
                                        : 0
                                    }}
                                  </div>
                                  <div class="attendance-stat-label">
                                    {{ $t("Absent") }}
                                  </div>
                                </div>
                                <div class="attendance-stat-divider"></div>
                                <div class="attendance-stat-col text-center">
                                  <div
                                    class="attendance-stat-value text-primary font-weight-bolder"
                                  >
                                    {{
                                      att.totalDays != null
                                        ? att.totalDays
                                        : 0
                                    }}
                                  </div>
                                  <div class="attendance-stat-label">
                                    {{ $t("Total Days") }}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </div>
                    </template>
                  </div>
                </b-tab>
              </b-tabs>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Error State -->
    <b-card no-body class="student-card mb-2 text-center" v-else>
      <b-card-body class="p-5">
        <feather-icon
          icon="AlertCircleIcon"
          size="50"
          class="text-warning mb-2"
        />
        <h3 class="text-warning font-weight-bolder">
          {{ $t("No record found") }}
        </h3>
        <p class="text-muted">
          {{ $t("Please try accessing this student via the Grid.") }}
        </p>
        <b-button variant="primary" @click="goBack">
          {{ $t("Go Back") }}
        </b-button>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BBadge,
  BSpinner,
  BAvatar,
  BImg,
  BTabs,
  BTab,
  VBTooltip,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import {
  parseStudentDocsPayload,
  parseDocTypesPayload,
  mergeStudentDocs,
  resolveTypeWiseDocuments,
} from "./studentDocsUtils";
import {
  getMediaPreviewUrl,
  handleDrivePreviewError,
  isImageUrl,
} from "@/utils/gdriveUpload";
import { ZAKAT_COPY } from "./zakatEligibilityCopy";
import RequestDetails from "./RequestDetails.vue";

export default {
  name: "StudentDetails",
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BBadge,
    BSpinner,
    BAvatar,
    BImg,
    BTabs,
    BTab,
    RequestDetails,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  async created() {
    await this.loadDetail();
  },
  data() {
    return {
      detailLoading: false,
      docsLoading: false,
      activeDetailTab: 0,
      detail: null,
      studentDocs: { templates: [], uploaded: [] },
      docTypesCache: [],
      zakatLang: "en",
      attLoading: false,
      attRecords: [],
      attSummary: null,
      months: [
        { id: 1, name: "January" },
        { id: 2, name: "February" },
        { id: 3, name: "March" },
        { id: 4, name: "April" },
        { id: 5, name: "May" },
        { id: 6, name: "June" },
        { id: 7, name: "July" },
        { id: 8, name: "August" },
        { id: 9, name: "September" },
        { id: 10, name: "October" },
        { id: 11, name: "November" },
        { id: 12, name: "December" },
      ],
    };
  },
  computed: {
    zakatContent() {
      return ZAKAT_COPY[this.zakatLang] || ZAKAT_COPY.en;
    },
    normalizedZakatEligible() {
      const value = this.studentDetails.zakatEligible;
      if (value === null || value === undefined || value === "") return "";
      return String(value).trim();
    },
    hasZakatResponse() {
      const value = this.normalizedZakatEligible.toLowerCase();
      return value === "yes" || value === "no";
    },
    zakatSelectedOptionText() {
      const value = this.normalizedZakatEligible.toLowerCase();
      if (value === "yes") return this.zakatContent.optionYes;
      if (value === "no") return this.zakatContent.optionNo;
      return (
        this.zakatContent.noResponse || this.$t("No response submitted yet.")
      );
    },
    zakatStatusLabel() {
      const value = this.normalizedZakatEligible;
      if (!value) return this.$t("N/A");
      return value;
    },
    zakatStatusVariant() {
      const value = this.normalizedZakatEligible.toLowerCase();
      if (value === "yes") return "light-success";
      if (value === "no") return "light-danger";
      return "light-secondary";
    },
    student() {
      return (this.detail && this.detail.student) || {};
    },
    studentDetails() {
      return (this.detail && this.detail.studentDetails) || {};
    },
    studentLeftRequests() {
      const requests = this.detail && this.detail.studentLeftRequests;
      return Array.isArray(requests) ? requests : [];
    },
    campusLabel() {
      const s = this.student;
      return (
        s.metaCampusName ||
        (s.school && s.school.branch) ||
        (s.cls_current &&
          s.cls_current.school &&
          s.cls_current.school.branch) ||
        ""
      );
    },
    federalRegLabel() {
      const reg = this.studentDetails.federalRegNo;
      if (!reg) return "";
      return `FB Reg# ${reg}`;
    },
    classLabel() {
      return this.student.meta_class || this.student.class_name || "";
    },
    commentText() {
      const c = this.studentDetails.comment;
      return c && String(c).trim() ? String(c).trim() : "";
    },
    statusIndicatorClass() {
      const s = String(this.studentDetails.studentStatus || "").toLowerCase();
      if (s === "active") return "bg-success";
      if (s === "on-probation") return "bg-warning";
      if (s === "in-active" || s === "inactive") return "bg-danger";
      return "bg-secondary";
    },
    statusIndicatorTitle() {
      return this.studentDetails.studentStatus || this.$t("Status unknown");
    },
    docSummary() {
      if (this.docsLoading || !this.typeWiseDocuments.length) return "";
      const uploaded = this.typeWiseDocuments.filter((doc) => !!doc.url).length;
      return `${uploaded}/${this.typeWiseDocuments.length} ${this.$t(
        "uploaded"
      )}`;
    },
    typeWiseDocuments() {
      return [...this.studentDocs.templates, ...this.studentDocs.uploaded];
      // const className =
      //   this.student.meta_class || this.student.class_name || "";
      // return resolveTypeWiseDocuments(
      //   this.studentDocs.templates,
      //   this.studentDocs.uploaded,
      //   this.docTypesCache,
      //   className
      // );
    },
    docStats() {
      const stats = {
        approved: 0,
        pending: 0,
        rejected: 0,
        notUploaded: 0,
        other: 0,
      };
      this.typeWiseDocuments.forEach((doc) => {
        if (!doc.url) {
          stats.notUploaded += 1;
          return;
        }
        const s = String(doc.status || "").toLowerCase();
        if (s === "approved") stats.approved += 1;
        else if (s === "pending") stats.pending += 1;
        else if (s === "rejected") stats.rejected += 1;
        else stats.other += 1;
      });
      return stats;
    },
    basicFields() {
      const s = this.student;
      return [
        {
          key: "name",
          label: this.$t("Student Name"),
          value: this.val(s.name),
        },
        {
          key: "father_name",
          label: this.$t("Father Name"),
          value: this.val(s.father_name),
        },
        { key: "gender", label: this.$t("Gender"), value: this.val(s.gender) },
        {
          key: "class",
          label: this.$t("Class"),
          value: this.val(s.meta_class || s.class_name),
        },
        {
          key: "contact_student",
          label: this.$t("Student's Contact"),
          value: this.val(s.contact_student),
        },
        {
          key: "contact_father",
          label: this.$t("WhatsApp No"),
          value: this.val(s.contact_father),
        },
        {
          key: "contact_mother",
          label: this.$t("Parent's Contact"),
          value: this.val(s.contact_mother),
        },
        {
          key: "studentCnic",
          label: this.$t("Student's CNIC No."),
          value: this.val(this.studentDetails.studentCnic),
        },
        {
          key: "emailAddress",
          label: this.$t("Email"),
          value: this.val(s.emailAddress),
        },
        {
          key: "last_result",
          label: this.$t("Student Category"),
          value: this.val(s.last_result),
        },
      ];
    },
    locationFields() {
      const d = this.studentDetails;
      return [
        { key: "region", label: this.$t("Region"), value: this.val(d.region) },
        { key: "area", label: this.$t("Area"), value: this.val(d.area) },
        {
          key: "campus",
          label: this.$t("Current Campus"),
          value: this.val(this.campusLabel),
        },
        {
          key: "branchCode",
          label: this.$t("Branch Code"),
          value: this.val(d.branchCode),
        },
        {
          key: "instituteName",
          label: this.$t("Institute Name"),
          value: this.val(d.instituteName),
        },
      ];
    },
    academicFields() {
      const d = this.studentDetails;
      const s = this.student;

      return [
        {
          key: "federalRegNo",
          label: this.$t("Federal Board Registration No"),
          value: this.val(d.federalRegNo),
        },
        {
          key: "schoolID",
          label: this.$t("School ID"),
          value: this.val(d.schoolID),
        },
        { key: "school", label: this.$t("School"), value: this.val(d.school) },
        {
          key: "matricRollNo",
          label: this.$t("Matric Roll No."),
          value: this.val(d.matricRollNo),
        },
        {
          key: "matricBoard",
          label: this.$t("Matric Board"),
          value: this.val(d.matricBoard),
        },
        {
          key: "matricPerc",
          label: this.$t("Matric %"),
          value: this.formatPercent(d.matricPerc),
        },
        {
          key: "matricGrade",
          label: this.$t("Matric Grade"),
          value: this.val(d.matricGrade),
        },
        {
          key: "ninthPerc",
          label: this.$t("9th %"),
          value: this.formatPercent(d.ninthPerc),
        },
        {
          key: "hsstPerc",
          label: this.$t("HSST %"),
          value: this.formatPercent(d.hsstPerc),
        },
        {
          key: "overallPerc",
          label: this.$t("Overall Score (HSST + Matric)"),
          value: this.formatPercent(d.overallPerc),
        },
        {
          key: "hsstStatus",
          label: this.$t("HSST Status"),
          value: this.val(d.hsstStatus),
        },
        {
          key: "interFaculty",
          label: this.$t("Inter Faculty"),
          value: this.val(this.formatClass(s.meta_class)),
        },
        {
          key: "intermediateBoard",
          label: this.$t("Intermediate Board"),
          value: this.val(d.intermediateBoard),
        },
        {
          key: "college",
          label: this.$t("College"),
          value: this.val(d.college),
        },
        {
          key: "firstYearRollNo",
          label: this.$t("1st Year Roll Number"),
          value: this.val(d.firstYearRollNo),
        },
        {
          key: "firstYearPerc",
          label: this.$t("1st Year %"),
          value: this.formatPercent(d.firstYearPerc),
        },
        {
          key: "paperPassed",
          label: this.$t("1st Year Paper Passed"),
          value: this.val(d.paperPassed),
        },
        {
          key: "secondYearRollNo",
          label: this.$t("Second Year Roll No"),
          value: this.val(d.secondYearRollNo),
        },
        {
          key: "secondYearPerc",
          label: this.$t("Second Year %"),
          value: this.formatPercent(d.secondYearPerc),
        },
      ];
    },
    statusFields() {
      const d = this.studentDetails;
      const s = this.student;

      const stfields = [
        {
          key: "fiscalYear",
          label: this.$t("Fiscal Year"),
          value: this.val(d.fiscalYear),
        },
        {
          key: "supportedYears",
          label: this.$t("Supported Years"),
          value: this.val(d.supportedYears),
        },
        {
          key: "scholarshipCategory",
          label: this.$t("Scholarship Category"),
          value: this.val(d.scholarshipCategory),
        },
        {
          key: "scholarshipStatus",
          label: this.$t("Scholarship Confirmation"),
          value: this.val(d.scholarshipStatus),
          badge: true,
          badgeVariant: this.scholarshipVariant(d.scholarshipStatus),
        },
        {
          key: "studentStatus",
          label: this.$t("Status"),
          value: this.val(d.studentStatus),
          badge: true,
          badgeVariant: this.statusVariant(d.studentStatus),
        },
        {
          key: "zakatEligible",
          label: this.$t("Zakat Eligibility"),
          value: this.zakatStatusLabel,
          badge: true,
          badgeVariant: this.zakatStatusVariant,
        },
        {
          key: "avgAttendance",
          label: this.$t("Avg Att."),
          value: this.val(d.avgAttendance),
          badge: true,
          badgeVariant: "light-primary",
        },
      ];

      if (s.status == "left") {
        stfields.push(
          {
            key: "date_of_leaving",
            label: this.$t("Leaving Date"),
            value: this.val(s.date_of_leaving),
            badge: true,
            badgeVariant: "light-danger",
            isDate: true,
          },
          {
            key: "last_note",
            label: this.$t("Leaving Reason"),
            value: this.val(s.last_note),
            badge: true,
            badgeVariant: "light-danger",
          }
        );
      }
      return stfields;
    },
    hasUtpDetails() {
      const d = this.studentDetails;
      if (!d) return false;
      const support = String(d.utpSupport || "")
        .trim()
        .toLowerCase();
      const status = String(d.utpStatus || "").trim();
      return support === "yes" || status !== "";
    },
    utpFields() {
      const d = this.studentDetails;
      const fields = [
        {
          key: "utpSupport",
          label: this.$t("UTP Support"),
          value: this.val(d.utpSupport),
          badge: !!d.utpSupport,
          badgeVariant:
            String(d.utpSupport || "").toLowerCase() === "yes"
              ? "light-success"
              : "light-secondary",
        },
        {
          key: "utpFiscalYear",
          label: this.$t("UTP Fiscal Year"),
          value: this.val(d.utpFiscalYear),
        },
        {
          key: "utpCourse",
          label: this.$t("UTP Course"),
          value: this.val(d.utpCourse),
        },
        {
          key: "utpInstituteID",
          label: this.$t("UTP Institute ID"),
          value: this.val(d.utpInstituteID),
        },
        {
          key: "utpInstituteName",
          label: this.$t("UTP Institute"),
          value: this.val(d.utpInstituteName),
        },
        {
          key: "utpLockedProgram1",
          label: this.$t("1. Locked Program"),
          value: this.val(d.utpLockedProgram1),
        },
        {
          key: "utpLockedTier1",
          label: this.$t("1. Locked Tier"),
          value: this.val(d.utpLockedTier1),
        },
        {
          key: "utpLockedProgram2",
          label: this.$t("2. Locked Program"),
          value: this.val(d.utpLockedProgram2),
        },
        {
          key: "utpLockedTier2",
          label: this.$t("2. Locked Tier"),
          value: this.val(d.utpLockedTier2),
        },
        {
          key: "utpClassMode",
          label: this.$t("Mode of Class"),
          value: this.val(d.utpClassMode),
        },
        {
          key: "utpQarzehasnaStatus",
          label: this.$t("Qarz-e-hasna Form Status"),
          value: this.val(d.utpQarzehasnaStatus),
          badge: !!d.utpQarzehasnaStatus,
          badgeVariant:
            String(d.utpQarzehasnaStatus || "").toLowerCase() === "submitted"
              ? "light-success"
              : "light-info",
        },
        {
          key: "utpStatus",
          label: this.$t("UTP Status"),
          value: this.val(d.utpStatus),
          badge: !!d.utpStatus,
          badgeVariant: this.statusVariant(d.utpStatus),
        },
        {
          key: "utpComment",
          label: this.$t("UTP Comment"),
          value: this.val(d.utpComment),
        },
      ];

      // if (d.utpLockedProgram3 || d.utpLockedTier3) {
      //   fields.push(
      //     {
      //       key: "utpLockedProgram3",
      //       label: this.$t("3. Locked Program"),
      //       value: this.val(d.utpLockedProgram3),
      //     },
      //     {
      //       key: "utpLockedTier3",
      //       label: this.$t("3. Locked Tier"),
      //       value: this.val(d.utpLockedTier3),
      //     }
      //   );
      // }

      return fields;
    },
  },
  methods: {
    ...mapActions({ get: "get" }),

    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: "manage-studentgrid" });
      }
    },

    val(value) {
      if (value === null || value === undefined || value === "") return "—";
      return String(value);
    },

    formatPercent(value) {
      if (value === null || value === undefined || value === "") return "—";
      const n = Number(value);
      if (Number.isNaN(n)) return String(value);
      if (n === 0) return "0";
      return String(n);
    },

    formatClass(metaVal) {
      const metaClass = String(metaVal || "").trim();
      if (metaClass.includes(" - ")) {
        const parts = metaClass.split(" - ");
        let className = (parts[0] || "").trim();
        let sectionName = "";
        if (parts.length > 1) {
          sectionName = parts.slice(1).join(" - ").trim();
        }
        return sectionName ? sectionName : className;
      } else return metaClass;
    },

    getMonthName(mID) {
      const monthVal = this.months.find((el) => el.id == mID);
      return monthVal ? monthVal.name.substring(0, 3) : "";
    },

    getAttendanceColorClass(percentage) {
      const p = Number(percentage) || 0;
      if (p >= 75) return "success";
      if (p >= 55) return "warning";
      return "danger";
    },

    formatAttendancePercentage(percentage) {
      if (
        percentage === null ||
        percentage === undefined ||
        percentage === ""
      ) {
        return "0";
      }
      const p = Number(percentage);
      if (Number.isNaN(p)) return "0";
      return p % 1 === 0 ? p.toFixed(0) : p.toFixed(2).replace(/\.?0+$/, "");
    },

    async loadDetail() {
      const id = this.$route.params.id;
      if (!id) return;

      this.detailLoading = true;
      const res = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/GetByStudentID?db=" +
          this.$store.state.userData.db +
          "&sID=" +
          id,
        token: this.$store.state.userData.token,
        context: this,
      });
      this.detailLoading = false;

      const payload =
        res && res.data && res.data.student
          ? res.data
          : res && res.student
          ? res
          : null;
      const c = payload && payload.student;
      const d = payload && payload.studentDetails;

      if (c && d) {
        this.detail = payload;
        await this.loadDocuments(id);
        this.LoadAttendance(id);
      } else {
        this.detail = null;
      }
    },

    resolveStudentCampusID() {
      return (
        this.student.campusID ||
        this.studentDetails.campusID ||
        this.$store.state.userData.cId
      );
    },

    async loadDocTypes(campusID) {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "DocumentTypes/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          campusID +
          "&type=student",
        token: this.$store.state.userData.token,
        returnData: true,
      });
      return parseDocTypesPayload(res);
    },

    async loadDocuments(studentID) {
      this.docsLoading = true;
      try {
        const campusID = this.resolveStudentCampusID();
        const res = await this.get({
          url:
            this.$store.state.domain +
            "DocumentDetails/LoadStudentDocs?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            campusID +
            "&studentID=" +
            studentID,
          token: this.$store.state.userData.token,
          returnData: true,
        });

        let { templates, uploaded } = parseStudentDocsPayload(res);
        this.studentDocs = {
          templates,
          uploaded,
        };
        // const docTypes = await this.loadDocTypes(campusID);
        // this.docTypesCache = docTypes;
        // this.studentDocs = mergeStudentDocs(templates, uploaded);
      } catch (e) {
        console.error("Load student docs error:", e);
      } finally {
        this.docsLoading = false;
      }
    },

    async LoadAttendance(studentID) {
      this.attLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "AttendanceSummary/GetByStudent?db=" +
            this.$store.state.userData.db +
            "&sID=" +
            studentID,
          token: this.$store.state.userData.token,
          returnData: true,
        });

        // console.log(res);
        if (res.data && Array.isArray(res.data)) {
          this.attRecords = res.data;
        } else this.attRecords = [];

        this.attSummary = res.summary ? res.summary : null
      } catch (e) {
        console.error("Load student docs error:", e);
      } finally {
        this.attLoading = false;
      }
    },

    statusVariant(status) {
      if (!status) return "secondary";
      const s = String(status).toLowerCase();
      if (s === "active") return "light-success";
      if (s === "in-active" || s === "inactive") return "light-danger";
      if (s === "on-probation") return "light-warning";
      return "secondary";
    },

    scholarshipVariant(status) {
      if (!status) return "secondary";
      const s = String(status).toLowerCase();
      if (s === "accepted") return "light-success";
      if (s === "not-accepted" || s === "not accepted") return "light-danger";
      if (s === "pending") return "light-warning";
      return "secondary";
    },

    docStatusVariant(doc) {
      if (!doc || !doc.url) return "light-secondary";
      const s = (doc.status || "").toLowerCase();
      if (s === "approved") return "light-success";
      if (s === "pending") return "light-warning";
      if (s === "rejected") return "light-danger";
      return "light-info";
    },

    isImage(url) {
      return isImageUrl(url);
    },

    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },

    getDocPreviewUrl(url) {
      return getMediaPreviewUrl(url, "Image");
    },

    onDocPreviewError(event, url) {
      if (!url) return;
      handleDrivePreviewError(event, url, "image");
    },

    openFile(url) {
      if (url) window.open(url, "_blank");
    },

    docTitle(doc) {
      return doc.meta_title || doc.title || this.$t("Document");
    },

    docStatusLabel(doc) {
      const status = (doc.status || "").trim();
      if (status) return status;
      return doc.url ? this.$t("Uploaded") : this.$t("Not Uploaded");
    },
  },
};
</script>

<style lang="scss" scoped>
.student-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.gradient-top-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  z-index: 10;
}

.profile-sidebar {
  border-right: 1px solid #f0edff;
}

.profile-banner {
  width: 100%;
  height: 120px;
  background: linear-gradient(135deg, #dfd8ff 0%, #f3f0ff 100%);
  position: relative;
  z-index: 0;
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
  padding: 6px;
  border-radius: 50%;
  background: #ffffff;
  z-index: 2;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.profile-avatar {
  border: 3px solid #f0edff;
}

.status-indicator {
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  z-index: 5;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.text-primary-dark {
  color: #2d1b69;
}

.name-title {
  letter-spacing: -0.3px;
  font-size: 1.5rem;
  margin-top: 5px;
}

.designation-text {
  font-size: 0.85rem;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  font-weight: 700;
  color: #7c5dfa !important;
  margin-bottom: 1.2rem;
}

.info-pill {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: #fdfcff;
  border: 1px solid #f0edff;
  padding: 10px 16px;
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #4b5563;
  transition: all 0.3s ease;
  word-break: break-word;
  line-height: 1.3;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
    border-color: #d4c7ff;
    background: #ffffff;
  }
}

.section-title {
  font-weight: 700;
  color: #2d1b69;
  border-bottom: 2px dashed #f0edff;
  padding-bottom: 10px;
  margin-bottom: 18px;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
}

.info-label {
  font-size: 0.75rem;
  color: #82868b;
  margin-bottom: 4px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.info-value {
  font-size: 0.95rem;
  color: #3f4254;
  font-weight: 600;
  line-height: 1.4;
}

.content-grid {
  background: #ffffff;
  padding: 20px 15px;
  border-radius: 16px;
  border: 1px solid #f0edff;
  box-shadow: 0 4px 20px rgba(115, 103, 240, 0.03);
  margin-bottom: 2rem !important;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 25px rgba(115, 103, 240, 0.08);
  }
}

.back-btn {
  border-radius: 10px;
  padding: 8px 14px;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  background: #ffffff;
  color: #7c5dfa !important;
  border: 1px solid #e0d8ff;

  &:hover {
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
    color: #ffffff !important;
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(124, 93, 250, 0.2) !important;
  }
}

::v-deep .student-details-tabs-shell {
  margin-top: 0.25rem;
}

::v-deep .student-details-tabs-shell .student-details-tab-panes {
  padding-top: 0.5rem;
  min-height: 120px;
}

::v-deep .student-details-tabs-shell .custom-nav-tabs {
  border-bottom: 2px solid #f0edff;
  margin-bottom: 0.75rem;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  gap: 0.25rem;
  padding-bottom: 2px;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbb9ff;
    border-radius: 4px;
  }

  .nav-item {
    flex: 0 0 auto;
    margin-bottom: -2px;

    .nav-link {
      border: none !important;
      color: #6b7280;
      font-weight: 600;
      font-size: 0.875rem;
      padding: 0.65rem 1rem;
      margin-right: 0;
      border-radius: 10px 10px 0 0;
      transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;
      background: transparent;
      white-space: nowrap;
      cursor: pointer;

      &:hover {
        color: #7c5dfa;
        background: #f8f6ff;
      }

      &.active,
      &.custom-nav-active {
        color: #5b21b6 !important;
        background: linear-gradient(
          180deg,
          #f5f3ff 0%,
          #ede9fe 100%
        ) !important;
        border-bottom: 3px solid #7c5dfa !important;
        font-weight: 700;
      }
    }
  }
}

.empty-state-box {
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px dashed #e5e7eb;
}

.empty-state-icon {
  color: #cbb9ff !important;
}

.empty-state-title {
  font-weight: 700;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

.empty-state-message {
  font-size: 1rem;
  font-weight: 500;
}

.request-block {
  background: #ffffff;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  padding: 0.75rem;
}

.request-block--spaced {
  margin-top: 1rem;
}

.request-block__heading {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7367f0;
}

.docs-subtitle {
  font-size: 0.875rem;
}

.docs-header {
  gap: 1rem;
}

.docs-stats {
  gap: 0.5rem;
}

.docs-stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  border: 1px solid transparent;
}

.docs-stat-pill--total {
  background: #f5f3ff;
  color: #5b21b6;
  border-color: #ddd6fe;
}

.docs-stat-pill--approved {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.docs-stat-pill--pending {
  background: #fffbeb;
  color: #b45309;
  border-color: #fde68a;
}

.docs-stat-pill--rejected {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}

.docs-stat-pill--missing {
  background: #f3f4f6;
  color: #4b5563;
  border-color: #e5e7eb;
}

.docs-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.docs-type-card {
  background: #ffffff;
  border: 1px solid #ece8ff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(115, 103, 240, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.docs-type-card--missing {
  border-style: dashed;
  border-color: #d1d5db;
  box-shadow: none;
}

.docs-type-card--uploaded:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(115, 103, 240, 0.12);
}

.docs-type-card__preview {
  position: relative;
  height: 130px;
  background: linear-gradient(180deg, #f8f6ff 0%, #f0edff 100%);
  overflow: hidden;
}

.docs-type-card--missing .docs-type-card__preview {
  background: #f9fafb;
}

.docs-type-card__preview--clickable {
  cursor: pointer;
}

.docs-type-card__img {
  width: 100%;
  height: 130px;
  object-fit: cover;
}

.docs-type-card__file-icon,
.docs-type-card__empty {
  width: 100%;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.docs-type-card__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(45, 27, 105, 0.65);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.docs-type-card__preview--clickable:hover .docs-type-card__overlay {
  opacity: 1;
}

.docs-type-card__body {
  padding: 0.75rem 0.85rem 0.85rem;
  text-align: center;
}

.docs-type-card__title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #2d1b69;
  line-height: 1.35;
  margin-bottom: 0.45rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.2rem;
}

.docs-open-btn {
  font-size: 0.78rem;
  font-weight: 700;
  border-radius: 8px;
}

.empty-docs-icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  border: 2px dashed #c4b5fd;
  display: flex;
  align-items: center;
  justify-content: center;
}

.docs-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}

.docs-gallery-card {
  background: #ffffff;
  border: 1px solid #ece8ff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.08);
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 32px rgba(115, 103, 240, 0.14);
    border-color: #cbb9ff;
  }
}

.docs-gallery-card__preview {
  position: relative;
  height: 160px;
  background: linear-gradient(180deg, #f8f6ff 0%, #f0edff 100%);
  overflow: hidden;
  cursor: pointer;
}

.docs-gallery-card__img {
  width: 100%;
  height: 160px;
  object-fit: cover;
}

.docs-gallery-card__file-icon,
.docs-gallery-card__empty {
  width: 100%;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.docs-gallery-card__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  background: rgba(45, 27, 105, 0.72);
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.docs-gallery-card__preview:hover .docs-gallery-card__overlay {
  opacity: 1;
}

.docs-gallery-card__body {
  padding: 0.9rem 1rem 1rem;
}

.docs-gallery-card__title {
  font-size: 0.92rem;
  font-weight: 700;
  color: #2d1b69;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.5rem;
}

.docs-status-badge {
  font-size: 0.65rem;
  padding: 0.35rem 0.55rem;
  letter-spacing: 0.03em;
}

.docs-open-btn {
  border-radius: 8px;
  padding: 0.25rem 0.65rem;
  font-size: 0.72rem;
  font-weight: 700;
}

.animation-fade {
  animation: fadeIn 0.4s ease-out;
}

.skeleton-loader {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

.gap-2 {
  gap: 0.5rem;
}

.scrollable-tab-content {
  max-height: 65vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 15px;
}

.scrollable-tab-content::-webkit-scrollbar {
  width: 6px;
}
.scrollable-tab-content::-webkit-scrollbar-track {
  background: transparent;
}
.scrollable-tab-content::-webkit-scrollbar-thumb {
  background: #dbdbdb;
  border-radius: 6px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes slideUpFade {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.zakat-subtitle {
  font-size: 0.875rem;
}

.zakat-header__title {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.zakat-panel--rtl {
  direction: rtl;
  text-align: right;

  .zakat-header {
    direction: ltr;
  }

  .zakat-header__text {
    order: 2;
    text-align: right;
  }

  .zakat-header__toggle {
    order: 1;
  }

  .zakat-header__title {
    display: inline-flex;
    align-items: center;
    flex-direction: row-reverse;
    gap: 0.35rem;
  }

  .zakat-status-card {
    justify-content: flex-end;
  }

  .zakat-status-card__item {
    text-align: right;
  }

  .zakat-criteria-item {
    padding-left: 0;
    padding-right: 1.1rem;

    &::before {
      left: auto;
      right: 0;
    }
  }

  .zakat-sub-list {
    padding: 0.35rem 0.85rem 0 0;

    li {
      padding-left: 0;
      padding-right: 0.95rem;

      &::before {
        left: auto;
        right: 0;
      }
    }
  }

  .zakat-response-display {
    text-align: right;
  }
}

.zakat-lang-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem;
  border-radius: 999px;
  background: #f5f3ff;
  border: 1px solid #e9e3ff;
}

.zakat-lang-btn {
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;

  &--active {
    background: #ffffff;
    color: #5b21b6;
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.12);
  }
}

.zakat-status-card {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-radius: 14px;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e9e3ff;
}

.zakat-status-card__item {
  min-width: 180px;
}

.zakat-status-card__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a86b7;
  margin-bottom: 0.35rem;
}

.zakat-status-card__value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2d1b69;
}

.zakat-form-card {
  background: #ffffff;
  border: 1px solid #ece8ff;
  border-radius: 16px;
  padding: 1.1rem;
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.06);
}

.zakat-info-box {
  padding: 0.95rem 1rem;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8f6ff 0%, #f3f0ff 100%);
  border: 1px solid #ddd6fe;
  color: #43308b;
  font-size: 0.92rem;
  line-height: 1.55;
  margin-bottom: 1rem;
}

.zakat-criteria-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.25rem;
}

.zakat-criteria-item {
  position: relative;
  padding-left: 1.1rem;
  margin-bottom: 0.85rem;
  color: #374151;
  line-height: 1.55;
  font-size: 0.92rem;

  &::before {
    content: "•";
    position: absolute;
    left: 0;
    top: 0;
    color: #7c5dfa;
    font-weight: 700;
  }
}

.zakat-criteria-item__main {
  font-weight: 600;
  color: #2d1b69;
}

.zakat-sub-list {
  list-style: none;
  padding: 0.35rem 0 0 0.85rem;
  margin: 0;

  li {
    position: relative;
    padding-left: 0.95rem;
    margin-bottom: 0.35rem;
    color: #4b5563;
    font-size: 0.88rem;

    &::before {
      content: "○";
      position: absolute;
      left: 0;
      top: 0;
      color: #9d7aff;
      font-size: 0.75rem;
    }
  }
}

.zakat-response-display {
  padding: 0.95rem 1rem;
  border-radius: 12px;
  border: 1.5px solid #e5e7eb;
  background: #f9fafb;
  color: #4b5563;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.55;

  &--yes {
    border-color: #a7f3d0;
    background: #ecfdf5;
    color: #047857;
  }

  &--no {
    border-color: #fecaca;
    background: #fef2f2;
    color: #b91c1c;
  }

  &--na {
    border-style: dashed;
    color: #6b7280;
    font-weight: 500;
  }
}

/* Attendance Summary Card */
.attendance-summary-card {
  position: relative;
  background: linear-gradient(135deg, #2d1b69 0%, #442a8b 50%, #7367f0 100%);
  border-radius: 20px;
  padding: 1.5rem 1.5rem 1.4rem;
  box-shadow: 0 12px 32px rgba(115, 103, 240, 0.28);
  color: #ffffff;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.summary-card-inner {
  position: relative;
  z-index: 2;
}

.summary-bg-shape {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 1;

  &--1 {
    width: 220px;
    height: 220px;
    top: -80px;
    right: -50px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 70%);
  }

  &--2 {
    width: 180px;
    height: 180px;
    bottom: -70px;
    left: 20%;
    background: radial-gradient(circle, rgba(157, 122, 255, 0.25) 0%, rgba(157, 122, 255, 0) 70%);
  }
}

.summary-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.summary-subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.82rem;
  font-weight: 400;
}

.summary-rate-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 6px 14px;
  border-radius: 999px;
  backdrop-filter: blur(6px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}

.summary-rate-label {
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.85);
  margin-right: 8px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
}

.summary-rate-val {
  font-size: 1rem;
  font-weight: 800;
  color: #38ef7d; 
  letter-spacing: -0.2px;
}

.summary-metric-pill {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  backdrop-filter: blur(8px);
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-2px);
  }
}

.summary-metric-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;

  &--rate {
    background: rgba(255, 178, 36, 0.195);
    color: #ff9f43;
  }

  &--present {
    background: rgba(40, 199, 111, 0.2);
    color: #38ef7d;
  }

  &--absent {
    background: rgba(234, 84, 84, 0.25);
    color: #ff7675;
  }

  &--days {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }
}

.summary-metric-value {
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.15;
  color: #ffffff;
  margin-bottom: 2px;
}

.summary-metric-label {
  font-size: 0.74rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
}

/* Attendance Breakdown Cards */
.attendance-section-accent {
  display: inline-block;
  width: 4px;
  height: 20px;
  background: #7367f0;
  border-radius: 3px;
  vertical-align: middle;
}

.attendance-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #ebe9f1;
  border-radius: 18px;
  padding: 1.25rem 1.25rem;
  box-shadow: 0 4px 18px rgba(115, 103, 240, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(115, 103, 240, 0.1);
  }

  &::before {
    content: "";
    position: absolute;
    top: 14px;
    bottom: 14px;
    left: 0;
    width: 4px;
    border-radius: 0 4px 4px 0;
  }

  &--success::before {
    background: #28c76f;
  }

  &--warning::before {
    background: #ff9f43;
  }

  &--danger::before {
    background: #ea5455;
  }
}

.attendance-icon-wrap {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--success {
    background: #ebfbf2;
    color: #28c76f;
  }

  &--warning {
    background: #fff4ea;
    color: #ff9f43;
  }

  &--danger {
    background: #fdf0f0;
    color: #ea5455;
  }
}

.attendance-circle-wrap {
  position: relative;
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.attendance-circular-chart {
  display: block;
  width: 100%;
  height: 100%;

  .circle-bg {
    fill: none;
    stroke: #eef1f5;
    stroke-width: 3.2;
  }

  .circle {
    fill: none;
    stroke-width: 3.4;
    stroke-linecap: round;
    transition: stroke-dasharray 0.3s ease;

    &--success {
      stroke: #28c76f;
    }

    &--warning {
      stroke: #ff9f43;
    }

    &--danger {
      stroke: #ea5455;
    }
  }
}

.attendance-circle-percentage {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.65rem;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;

  &.text--success {
    color: #28c76f;
  }

  &.text--warning {
    color: #ff9f43;
  }

  &.text--danger {
    color: #ea5455;
  }
}

.attendance-stats-box {
  background: #f8f9fc;
  border-radius: 12px;
  padding: 10px 8px;
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.attendance-stat-col {
  flex: 1;
  min-width: 0;
}

.attendance-stat-value {
  font-size: 1.15rem;
  line-height: 1.2;
  margin-bottom: 2px;
}

.attendance-stat-label {
  color: #6e6b7b;
  font-size: 0.75rem;
  font-weight: 500;
}

.attendance-stat-divider {
  width: 1px;
  height: 26px;
  background: #e9ecef;
}

.unused-zakat-rule {
  .unused-inner {
    border-style: dashed;
    color: #6b7280;
    font-weight: 500;
  }
}
</style>
